/* ============================================================
   Preparo da foto, no próprio aparelho

   Duas saídas de UMA decodificação:
     - `photo`  1080 px, é o que se vê ao abrir o prato
     - `thumb`   320 px quadrada, é o que o feed carrega

   A decodificação única não é otimização prematura: no app anterior as
   saídas eram geradas em paralelo, cada uma decodificando o arquivo
   original de novo. Numa câmera de 50 MP isso é decodificar três imagens
   gigantes ao mesmo tempo, e num aparelho sem RAM sobrando as três falham
   juntas. Foi o bug que impediu um membro do grupo de postar por dias.

   Sai Blob, não data URL. Base64 é 33% maior e só existia porque a foto
   ia para dentro do documento do Firestore — aqui ela vai para o Storage,
   que aceita bytes.
   ============================================================ */

const TRABALHO_MAX = 1080;

/** WebP quando o navegador sabe; JPEG no resto. */
const MIME: string = (() => {
  const c = document.createElement("canvas");
  c.width = c.height = 1;
  return c.toDataURL("image/webp").startsWith("data:image/webp") ? "image/webp" : "image/jpeg";
})();

export const EXTENSAO = MIME === "image/webp" ? "webp" : "jpg";

export class ImagemInvalida extends Error {
  /** Curto, pra caber numa linha de tela e num print de suporte. */
  constructor(message: string, readonly codigo = "imagem-invalida") {
    super(message);
    this.name = "ImagemInvalida";
  }
}

/* ============================================================
   OS BYTES SÃO COPIADOS NO INSTANTE DA ESCOLHA

   Um `File` vindo do seletor do Android é só um ponteiro pra um
   `content://`. Nome, tipo e tamanho vêm do índice da galeria e
   sobrevivem a tudo; os bytes só são lidos quando alguém pede — e até lá
   o provedor pode ter soltado a permissão, reciclado o temporário, ou
   nem ter o arquivo ainda (foto que a galeria mostra mas que continua na
   nuvem).

   O sintoma engana porque os metadados chegam certos: "não consegui ler
   essa imagem (image/jpeg, 93 KB)", o tamanho exato impresso por cima de
   zero byte legível. Nos dois relatos a MESMA foto funcionou na segunda
   escolha, sem nada ter mudado no arquivo — e funcionava sempre ao tirar
   pelo app ou ao cortar na galeria, que é quando o arquivo é novo e
   local.

   Por isso `capturar()` chama `arrayBuffer()` ANTES de o `<input>` ser
   solto, e o resto do app trabalha sobre o `Blob` de memória. Ler alguns
   MB de JPEG pra RAM não é o consumo que o comentário do topo deste
   arquivo evita: aquele é o bitmap DECODIFICADO, duas ordens de grandeza
   maior. Não troque isto por uma leitura preguiçosa de novo.
   ============================================================ */

/**
 * Um corte, em frações da foto (0..1), já com a orientação do EXIF
 * aplicada — é o que o `<img>` mostra e o `createImageBitmap` entrega.
 */
/**
 * O pedaço escolhido no `Recortador`, em frações (0..1) da foto JÁ
 * GIRADA por `giro` graus (sentido horário).
 */
export interface Recorte { x: number; y: number; w: number; h: number; giro?: 0 | 90 | 180 | 270 }

/** Uma foto escolhida cujos bytes já estão sendo lidos. */
export interface FotoEscolhida {
  /** Guardado só pra uma segunda tentativa a partir do original. */
  file: File;
  bytes: Promise<Blob>;
  /** O pedaço que a pessoa escolheu em `Recortador`. Sem ele, a foto toda. */
  recorte?: Recorte;
}

/* Três tentativas curtas. A primeira pega o caso comum; as outras dão
   tempo pra uma foto que a galeria ainda estava baixando da nuvem
   terminar de chegar. */
const ESPERAS = [0, 250, 750];

async function lerBytes(file: File): Promise<Blob> {
  let ultimo = "vazio";
  for (const espera of ESPERAS) {
    if (espera) await new Promise((pronto) => setTimeout(pronto, espera));
    try {
      const buffer = await file.arrayBuffer();
      if (buffer.byteLength > 0) return new Blob([buffer], { type: file.type });
      ultimo = "vazio";
    } catch (err) {
      ultimo = (err as Error)?.name || "erro";
    }
  }
  throw new ImagemInvalida(
    "Não consegui ler esta foto agora. Se ela veio de outro app ou ainda "
    + "está baixando na galeria, tente de novo; ou tire a foto aqui pelo app.",
    `leitura/${ultimo}`,
  );
}

/** Começa a leitura AGORA e devolve a foto com a promessa dos bytes. */
export function capturar(file: File): FotoEscolhida {
  const bytes = lerBytes(file);
  /* A fila de preparo só consome esta promessa daqui a segundos. Sem um
     consumidor agora, uma rejeição viraria `unhandledrejection` antes de
     alguém ter chance de tratá-la. */
  bytes.catch(() => {});
  return { file, bytes };
}

/* O que os primeiros bytes dizem — só consultado quando TUDO falhou.
   Serve pra separar "não consegui ler" de "li, e é um formato que este
   navegador não abre": o Chrome do Android não decodifica HEIC, e a
   câmera da Samsung grava HEIC com "fotos de alta eficiência" ligado.
   Sem isso as duas falhas dão a mesma frase e ninguém consegue dizer
   qual aconteceu. Silêncio ("") quando não dá pra afirmar nada — chute
   aqui vira mensagem errada na tela de quem só quer postar a janta. */
function formatoNaoAberto(cabeca: Uint8Array): string {
  const texto = (i: number, n: number) => String.fromCharCode(...cabeca.subarray(i, i + n));
  if (cabeca[0] === 0xFF && cabeca[1] === 0xD8) return "";           // JPEG
  if (cabeca[0] === 0x89 && texto(1, 3) === "PNG") return "";        // PNG
  if (texto(0, 3) === "GIF") return "";                              // GIF
  if (texto(0, 4) === "RIFF" && texto(8, 4) === "WEBP") return "";   // WebP
  if (texto(4, 4) === "ftyp") {
    /* Só as marcas conhecidas. Todo MP4 também começa com `ftyp`, e
       dizer "esta foto está em HEIC" pra um vídeo é trocar uma mensagem
       inútil por uma mentira. */
    const marca = texto(8, 4);
    if (/^(heic|heix|heim|heis|hevc|hevx|mif1|msf1)$/.test(marca)) return "HEIC";
    if (/^(avif|avis)$/.test(marca)) return "AVIF";
  }
  return "";
}

async function naoAbriu(foto: Blob, motivos: string[]): Promise<ImagemInvalida> {
  const cabeca = new Uint8Array(await foto.slice(0, 16).arrayBuffer());
  const formato = formatoNaoAberto(cabeca);
  const codigo = `decode/${formato || motivos.join(",") || "sem-motivo"}`;

  if (formato) {
    return new ImagemInvalida(
      `Esta foto está em ${formato}, um formato que o navegador do Android não `
      + "abre. Corte a foto na galeria — isso salva uma cópia comum — ou "
      + "desligue as fotos de alta eficiência na câmera.",
      codigo,
    );
  }
  return new ImagemInvalida(
    `Não consegui abrir esta foto (${Math.round(foto.size / 1024)} KB). `
    + "Tente cortar a foto na galeria, ou tire a foto aqui pelo app.",
    codigo,
  );
}

// O canvas de trabalho também entra como fonte: é dele que saem as duas
// saídas finais, sem tocar de novo no arquivo original.
type Fonte = ImageBitmap | HTMLImageElement | HTMLCanvasElement;

async function decodificar(foto: Blob): Promise<Fonte> {
  const motivos: string[] = [];

  // `from-image` respeita o EXIF (foto de celular deitada). Se a
  // decodificação cheia falhar por falta de memória, pede ao próprio
  // navegador para reduzir durante a decodificação, que custa bem menos.
  if (typeof createImageBitmap === "function") {
    for (const opcoes of [
      { imageOrientation: "from-image" },
      { imageOrientation: "from-image", resizeWidth: TRABALHO_MAX, resizeQuality: "medium" },
      { imageOrientation: "from-image", resizeHeight: TRABALHO_MAX, resizeQuality: "medium" },
    ] as ImageBitmapOptions[]) {
      try {
        return await createImageBitmap(foto, opcoes);
      } catch (err) {
        /* O motivo de cada tentativa é a única pista que sobra quando as
           quatro falham. Engoli-los foi o que deixou o primeiro relato
           sem diagnóstico possível à distância. */
        motivos.push((err as Error)?.name || "erro");
      }
    }
  }

  const url = URL.createObjectURL(foto);
  const img = new Image();
  img.decoding = "async";
  const abriu = await new Promise<boolean>((pronto) => {
    img.onload = () => pronto(true);
    img.onerror = () => pronto(false);
    img.src = url;
  });
  setTimeout(() => URL.revokeObjectURL(url), 1000);

  if (abriu) return img;
  throw await naoAbriu(foto, motivos);
}

/**
 * Recorta a fonte no retângulo escolhido, já limitando o tamanho: corta
 * e reduz na mesma passada, então a foto grande não é desenhada inteira
 * antes de ser cortada.
 */
function recortar(original: Fonte, r: Recorte | undefined, maxEdge: number): HTMLCanvasElement {
  const fonte = girada(original, r?.giro ?? 0);
  const W = fonte.width, H = fonte.height;
  const x = Math.round(Math.max(0, Math.min(1, r?.x ?? 0)) * W);
  const y = Math.round(Math.max(0, Math.min(1, r?.y ?? 0)) * H);
  const w = Math.max(1, Math.min(W - x, Math.round((r?.w ?? 1) * W)));
  const h = Math.max(1, Math.min(H - y, Math.round((r?.h ?? 1) * H)));
  const escala = Math.min(1, maxEdge / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(w * escala));
  canvas.height = Math.max(1, Math.round(h * escala));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new ImagemInvalida("Este navegador não deixou desenhar a imagem.");
  ctx.drawImage(fonte, x, y, w, h, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/** A foto girada em passos de 90°. Sem giro, a própria fonte (nada é copiado). */
function girada(fonte: Fonte, giro: number): Fonte {
  if (!giro) return fonte;
  const deitada = giro === 90 || giro === 270;
  const c = document.createElement("canvas");
  c.width = deitada ? fonte.height : fonte.width;
  c.height = deitada ? fonte.width : fonte.height;
  const ctx = c.getContext("2d");
  if (!ctx) throw new ImagemInvalida("Este navegador não deixou desenhar a imagem.");
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate((giro * Math.PI) / 180);
  ctx.drawImage(fonte, -fonte.width / 2, -fonte.height / 2);
  return c;
}

function desenhar(fonte: Fonte, maxEdge: number, quadrada: boolean): HTMLCanvasElement {
  const sw = fonte.width, sh = fonte.height;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new ImagemInvalida("Este navegador não deixou desenhar a imagem.");

  if (quadrada) {
    const lado = Math.min(maxEdge, sw, sh);
    canvas.width = canvas.height = lado;
    const corte = Math.min(sw, sh);
    ctx.drawImage(fonte, (sw - corte) / 2, (sh - corte) / 2, corte, corte, 0, 0, lado, lado);
  } else {
    const escala = Math.min(1, maxEdge / Math.max(sw, sh));
    canvas.width = Math.max(1, Math.round(sw * escala));
    canvas.height = Math.max(1, Math.round(sh * escala));
    ctx.drawImage(fonte, 0, 0, canvas.width, canvas.height);
  }
  return canvas;
}

const paraBlob = (canvas: HTMLCanvasElement, q: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new ImagemInvalida("Não consegui converter a foto."))),
      MIME,
      q,
    );
  });

/** Baixa a qualidade e depois a resolução até caber no teto. */
async function comprimir(
  fonte: HTMLCanvasElement,
  { maxEdge, maxBytes, quadrada = false }: { maxEdge: number; maxBytes: number; quadrada?: boolean },
): Promise<Blob> {
  let lado = maxEdge;
  for (let passo = 0; passo < 5; passo++) {
    const canvas = desenhar(fonte, lado, quadrada);
    for (const q of [0.82, 0.72, 0.6, 0.48]) {
      const blob = await paraBlob(canvas, q);
      if (blob.size <= maxBytes) return blob;
    }
    lado = Math.round(lado * 0.75);
  }
  return paraBlob(desenhar(fonte, 480, quadrada), 0.45);
}

export interface FotosDoPrato {
  photo: Blob;
  thumb: Blob;
  largura: number;
  altura: number;
}

export async function prepararFotos(escolha: FotoEscolhida): Promise<FotosDoPrato> {
  const original = await decodificar(await escolha.bytes);
  let trabalho: HTMLCanvasElement;
  try {
    trabalho = recortar(original, escolha.recorte, TRABALHO_MAX);
  } finally {
    // Libera a original ANTES de gerar qualquer saída.
    (original as ImageBitmap).close?.();
  }

  return {
    photo: await comprimir(trabalho, { maxEdge: 1080, maxBytes: 400 * 1024 }),
    thumb: await comprimir(trabalho, { maxEdge: 320, maxBytes: 40 * 1024, quadrada: true }),
    largura: trabalho.width,
    altura: trabalho.height,
  };
}

/**
 * Capa de desafio: larga, não quadrada.
 *
 * Separada do avatar de propósito. A capa é desenhada num quadro 16/9;
 * recortá-la em quadrado antes faria o navegador esticar de volta e
 * entregar uma faixa achatada com as pontas cortadas.
 */
export async function prepararCapa(escolha: FotoEscolhida): Promise<Blob> {
  const original = await decodificar(await escolha.bytes);
  let trabalho: HTMLCanvasElement;
  try {
    trabalho = desenhar(original, TRABALHO_MAX, false);
  } finally {
    (original as ImageBitmap).close?.();
  }
  return comprimir(trabalho, { maxEdge: 1080, maxBytes: 200 * 1024 });
}

/** Foto de perfil: uma saída só, quadrada e pequena. */
export async function prepararAvatar(escolha: FotoEscolhida): Promise<Blob> {
  const original = await decodificar(await escolha.bytes);
  let trabalho: HTMLCanvasElement;
  try {
    trabalho = recortar(original, escolha.recorte, 512);
  } finally {
    (original as ImageBitmap).close?.();
  }
  return comprimir(trabalho, { maxEdge: 256, maxBytes: 60 * 1024, quadrada: true });
}
